import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SITE from '../api';
import { usePortal, PORTAL } from '../portalAuth';
import { useSite, IMG } from '../components/StoreLayout';
import { ProductCard } from './Home';
import { TestimonialScroller } from './AboutPage';
import StorePhoneInput from '../components/StorePhoneInput';
import { STORE_BASE } from '../routeBase';
import Captcha from '../components/Captcha';
import useSEO from '../useSEO';
import { trackEvent } from '../analytics';

const CATEGORY = 'Pure Gold Frames';

function ytVideoId(url) {
  if (!url) return '';
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]{6,})/);
  return m ? m[1] : '';
}

export default function PureGoldFrames() {
  const { content: c } = useSite();
  const portal = usePortal();
  const customer = portal?.customer;
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [f, setF] = useState({ name: '', email: '', phone: '', message: '' });
  const [captchaToken, setCaptchaToken] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    // Logged-in customers get priced rows from the portal API (same pattern
    // as Home.js/Shop.js) — the public SITE endpoint never includes price.
    const client = customer ? PORTAL : SITE;
    client.get('/products', { params: { category: CATEGORY, limit: 8 } }).then(r => setProducts(r.data.products || []));
    SITE.get('/testimonials', { params: { page: 'pure-gold-frames' } }).then(r => setTestimonials(r.data.testimonials || []));
  }, [customer]);

  useEffect(() => {
    let slugs = [];
    try { slugs = JSON.parse(c['page.pure-gold-frames.featured_blogs'] || '[]'); } catch { /* ignore malformed content */ }
    if (!slugs.length) { setFeaturedBlogs([]); return; }
    SITE.get('/blogs').then(r => {
      const bySlug = new Map((r.data.blogs || []).map(b => [b.slug, b]));
      setFeaturedBlogs(slugs.map(s => bySlug.get(s)).filter(Boolean));
    });
  }, [c]);

  useSEO({
    title: c['page.pure-gold-frames.meta_title'] || c['page.pure-gold-frames.title'] || '18 Carat Pure Gold Frames',
    description: c['page.pure-gold-frames.meta_description'],
    canonical: `${window.location.origin}/store/page/pure-gold-frames`,
  });

  let benefits = [];
  try { benefits = JSON.parse(c['page.pure-gold-frames.benefits'] || '[]'); } catch { /* ignore malformed content */ }
  let faq = [];
  try { faq = JSON.parse(c['page.pure-gold-frames.faq'] || '[]'); } catch { /* ignore malformed content */ }
  const videoId = ytVideoId(c['page.pure-gold-frames.video_url']);

  const set = k => e => setF(prev => ({ ...prev, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault(); setErr('');
    if (!f.name || !f.email || !f.message) { setErr('Please enter your name, email and a short message'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return setErr('Please enter a valid email address');
    if (!f.phone || !f.phone.trim()) return setErr('Please enter your phone number');
    setBusy(true);
    try {
      await SITE.post('/enquiry', { ...f, source: 'pure-gold-frames', subject: '18 Carat Pure Gold Frames enquiry', captcha_token: captchaToken });
      setSent(true);
      trackEvent('wholesale_enquiry', { source: 'pure-gold-frames' });
    } catch (e2) { setErr(e2.response?.data?.error || 'Could not submit. Please try again.'); }
    setBusy(false);
  };

  const heroImg = c['page.pure-gold-frames.hero_image'] || c['homepage.home_banner_8'];

  return (
    <div style={{ background: '#0b0a08' }}>
      <style>{`
        @keyframes pgShimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
        @keyframes pgTwinkle { 0%, 100% { opacity: .15; transform: scale(0.8); } 50% { opacity: .9; transform: scale(1.15); } }
        .pg-shimmer-text { background: linear-gradient(90deg, #b8860b 0%, #f5d576 25%, #fff6d8 50%, #f5d576 75%, #b8860b 100%); background-size: 800px 100%; -webkit-background-clip: text; background-clip: text; color: transparent; animation: pgShimmer 6s linear infinite; }
        .pg-spark { position: absolute; border-radius: 50%; background: radial-gradient(circle, #ffe9a8 0%, rgba(184,134,11,0.5) 70%, transparent 100%); animation-name: pgTwinkle; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
        .pg-benefit-card { transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease; }
        .pg-benefit-card:hover { transform: translateY(-4px); box-shadow: 0 12px 34px rgba(184,134,11,0.25); border-color: rgba(184,134,11,0.6) !important; }
        .pg-input { width:100%; padding:11px 13px; border:1px solid rgba(184,134,11,0.35); border-radius:8px; font-size:14px; box-sizing:border-box; background:rgba(255,255,255,0.03); color:#f2e6c9; }
        .pg-input::placeholder { color: rgba(242,230,201,0.4); }
        .pg-input:focus { outline:none; border-color:#d4af37; box-shadow: 0 0 0 3px rgba(212,175,55,0.15); }
        .pg-gold-btn { background: linear-gradient(135deg, #d4af37 0%, #b8860b 60%, #916808 100%); color: #1a1300; border: none; font-weight: 700; letter-spacing: 0.4px; }
        .pg-gold-btn:hover { filter: brightness(1.08); }
        .pg-gold-outline { border: 1.5px solid #d4af37; color: #f5d576; background: transparent; }
        .pg-gold-outline:hover { background: rgba(212,175,55,0.12); }
        .pg-phone .PhoneInput { background: rgba(255,255,255,0.03) !important; border-color: rgba(184,134,11,0.35) !important; }
        .pg-phone .PhoneInputInput { background: transparent !important; color: #f2e6c9 !important; }
        .pg-phone .PhoneInputCountrySelectArrow { color: #cbbf9e !important; }
      `}</style>

      {/* HERO */}
      <section className="sf-fade-in" style={{
        position: 'relative', minHeight: 480, display: 'flex', alignItems: 'center', overflow: 'hidden',
        // Matches the 1600x700 "best size" hint given in the admin editor —
        // without this, a fluid-width section with only a minHeight floor
        // rarely has the same aspect ratio as the uploaded image, so
        // background-size:cover silently crops its top/bottom to fill
        // whatever height the box ends up with.
        ...(heroImg ? { aspectRatio: '1600 / 700' } : {}),
        background: heroImg
          ? `linear-gradient(180deg, rgba(6,5,3,.55), rgba(6,5,3,.85)), url(${IMG(heroImg)}) center/cover`
          : 'radial-gradient(circle at 75% 30%, #2a2110 0%, #14100a 45%, #0b0a08 100%)',
      }}>
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          {[...Array(22)].map((_, i) => (
            <span key={i} className="pg-spark" style={{
              left: `${(i * 13 + 4) % 96}%`, top: `${(i * 29 + 7) % 96}%`,
              width: 3 + (i % 3) * 2, height: 3 + (i % 3) * 2,
              animationDuration: `${2.4 + (i % 5) * 0.6}s`, animationDelay: `${(i % 7) * 0.4}s`,
            }} />
          ))}
        </div>

        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '60px 24px', position: 'relative', width: '100%', boxSizing: 'border-box', textAlign: 'center' }}>
          <span style={{ display: 'inline-block', border: '1px solid rgba(212,175,55,0.5)', color: '#e8c766', padding: '5px 18px', borderRadius: 20, fontSize: 11.5, fontWeight: 600, letterSpacing: 2, marginBottom: 22, textTransform: 'uppercase' }}>
            18 Carat &middot; Solid Gold
          </span>
          <h1 className="pg-shimmer-text" style={{ fontSize: 'clamp(32px,5vw,56px)', fontWeight: 800, lineHeight: 1.15, margin: '0 0 18px', letterSpacing: 0.5 }}>
            {c['page.pure-gold-frames.title'] || '18 Carat Pure Gold Frames'}
          </h1>
          <p style={{ fontSize: 17, color: '#cbbf9e', maxWidth: 560, lineHeight: 1.7, margin: '0 auto' }}>
            {c['page.pure-gold-frames.subtitle'] || 'Solid 18 carat gold, hand-finished for a rare few who accept nothing less than the real thing.'}
          </p>
          <a href="#pg-form" className="pg-gold-outline" style={{ display: 'inline-block', marginTop: 32, padding: '13px 34px', borderRadius: 26, fontWeight: 700, fontSize: 13.5, textDecoration: 'none', letterSpacing: 0.6, textTransform: 'uppercase' }}>
            Enquire Now
          </a>
        </div>
      </section>

      {/* INTRO / USAGE */}
      {c['page.pure-gold-frames'] && (
        <section style={{ maxWidth: 760, margin: '0 auto', padding: '64px 24px 0', textAlign: 'center' }}>
          <div className="sf-richtext" style={{ fontSize: 16, lineHeight: 1.9, color: '#cbbf9e' }} dangerouslySetInnerHTML={{ __html: c['page.pure-gold-frames'] }} />
        </section>
      )}

      {/* BENEFITS */}
      {benefits.length > 0 && (
        <section style={{ maxWidth: 1100, margin: '56px auto 0', padding: '0 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
            {benefits.map((b, i) => (
              <div key={i} className="pg-benefit-card" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,175,55,0.25)', borderRadius: 14, padding: '30px 24px', textAlign: 'center' }}>
                <div style={{ width: 54, height: 54, borderRadius: '50%', background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <i className={`ti ti-${b.icon}`} style={{ fontSize: 24, color: '#e8c766' }} />
                </div>
                <h3 style={{ fontSize: 15.5, fontWeight: 700, marginBottom: 10, color: '#f2e6c9' }}>{b.title}</h3>
                <p style={{ fontSize: 13, color: '#a99a72', lineHeight: 1.7, margin: 0 }}>{b.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* MODELS — warm gold "podium" panel behind the (light) product cards,
          echoing the pedestal look of the reference banner artwork. Capped at
          8 so the page doesn't run long; "Explore More Models" links to the
          full category on the Shop page for the rest. */}
      {products.length > 0 && (
        <section style={{ margin: '56px 0 0', padding: '64px 24px', background: 'linear-gradient(180deg, #1a1508 0%, #2a2110 50%, #1a1508 100%)' }}>
          <div style={{ maxWidth: 1300, margin: '0 auto' }}>
            <h2 className="pg-shimmer-text" style={{ fontSize: 28, fontWeight: 800, textAlign: 'center', marginBottom: 8 }}>Our Models</h2>
            {c['page.pure-gold-frames.models_intro'] && (
              <p style={{ textAlign: 'center', color: '#cbbf9e', fontSize: 14, maxWidth: 560, margin: '0 auto 32px' }}>{c['page.pure-gold-frames.models_intro']}</p>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 24 }}>
              {products.map(p => (
                <div key={p.model_no} style={{ width: 250, flex: '0 0 auto' }}>
                  <ProductCard p={p} />
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 44 }}>
              <Link to={`${STORE_BASE}/shop?category=${encodeURIComponent(CATEGORY)}`} className="pg-gold-outline"
                style={{ display: 'inline-block', padding: '13px 34px', borderRadius: 26, fontWeight: 700, fontSize: 13.5, textDecoration: 'none', letterSpacing: 0.6, textTransform: 'uppercase' }}>
                Explore More Models
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* VIDEO */}
      {videoId && (
        <section style={{ maxWidth: 900, margin: '72px auto 0', padding: '0 24px' }}>
          <h2 className="pg-shimmer-text" style={{ fontSize: 26, fontWeight: 800, textAlign: 'center', marginBottom: 28 }}>See The Craft In Motion</h2>
          <div style={{ width: '100%', maxWidth: 720, height: 0, paddingTop: '56.25%', margin: '0 auto', position: 'relative', borderRadius: 14, overflow: 'hidden', background: '#000', border: '1px solid rgba(212,175,55,0.3)' }}>
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=1&rel=0&modestbranding=1`}
              title="18 Carat Pure Gold Frames"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
              allow="autoplay; encrypted-media" allowFullScreen
            />
          </div>
        </section>
      )}

      {/* ENQUIRY FORM */}
      <section id="pg-form" style={{ maxWidth: 640, margin: '72px auto 0', padding: '0 24px' }}>
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,175,55,0.3)', borderRadius: 18, padding: 34 }}>
          {sent ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <i className="ti ti-circle-check" style={{ fontSize: 46, color: '#d4af37' }} />
              <h3 style={{ marginTop: 12, color: '#f2e6c9' }}>Thank you!</h3>
              <p style={{ color: '#a99a72', fontSize: 14 }}>Your enquiry has been received — our team will get in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h3 style={{ fontSize: 21, fontWeight: 700, marginBottom: 6, color: '#f2e6c9', textAlign: 'center' }}>Enquire About This Collection</h3>
              <p style={{ fontSize: 13.5, color: '#a99a72', marginBottom: 22, textAlign: 'center' }}>A limited, made-to-order line — tell us what you're looking for and we'll follow up personally.</p>
              {err && <div style={{ background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.35)', color: '#fca5a5', fontSize: 13, padding: '8px 11px', borderRadius: 8, marginBottom: 14 }}>{err}</div>}

              <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Name *</label>
                  <input className="pg-input" value={f.name} onChange={set('name')} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Email *</label>
                  <input className="pg-input" type="email" value={f.email} onChange={set('email')} required />
                </div>
              </div>
              <div className="pg-phone" style={{ marginBottom: 14 }}>
                <label style={lbl}>Phone *</label>
                <StorePhoneInput value={f.phone} onChange={v => setF(prev => ({ ...prev, phone: v }))} required />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Message *</label>
                <textarea className="pg-input" value={f.message} onChange={set('message')} rows={4} style={{ resize: 'vertical' }} />
              </div>

              <Captcha onToken={setCaptchaToken} />
              <button type="submit" disabled={busy} className="pg-gold-btn"
                style={{ width: '100%', padding: 13, borderRadius: 8, fontSize: 14, cursor: 'pointer', marginTop: 6 }}>
                {busy ? 'Submitting…' : 'Submit Enquiry'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section style={{ maxWidth: 1100, margin: '72px auto 0', padding: '0 24px' }}>
          <h2 className="pg-shimmer-text" style={{ fontSize: 26, fontWeight: 800, textAlign: 'center', marginBottom: 28 }}>What Our Clients Say</h2>
          <TestimonialScroller items={testimonials} />
        </section>
      )}

      {/* FAQ */}
      {faq.length > 0 && (
        <section style={{ maxWidth: 800, margin: '72px auto 0', padding: '0 24px' }}>
          <h2 className="pg-shimmer-text" style={{ fontSize: 26, fontWeight: 800, textAlign: 'center', marginBottom: 24 }}>Frequently Asked Questions</h2>
          {faq.map((f, i) => (
            <details key={i} style={{ borderTop: '1px solid rgba(212,175,55,0.25)', padding: '14px 0' }}>
              <summary style={{ fontSize: 14.5, fontWeight: 600, cursor: 'pointer', color: '#f2e6c9' }}>{f.question}</summary>
              <p style={{ fontSize: 13.5, color: '#a99a72', lineHeight: 1.7, marginTop: 8 }}>{f.answer}</p>
            </details>
          ))}
        </section>
      )}

      {/* BLOG */}
      <section style={{ maxWidth: 1100, margin: '72px auto 0', padding: '0 24px 90px' }}>
        <h2 className="pg-shimmer-text" style={{ fontSize: 26, fontWeight: 800, textAlign: 'center', marginBottom: 28 }}>From The Journal</h2>
        {featuredBlogs.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 22 }}>
            {featuredBlogs.map(b => (
              <Link key={b.slug} to={`${STORE_BASE}/blog/${b.slug}`} style={{ textDecoration: 'none', color: 'inherit', width: 280, flex: '0 0 auto' }}>
                <div className="sf-journal-card" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,175,55,0.25)', borderRadius: 12, overflow: 'hidden', height: '100%' }}>
                  <div style={{ height: 150, background: 'rgba(255,255,255,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {b.cover_image ? <img src={IMG(b.cover_image)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <i className="ti ti-article" style={{ fontSize: 36, color: 'rgba(212,175,55,0.4)' }} />}
                  </div>
                  <div style={{ padding: 16 }}>
                    <h3 style={{ fontSize: 15.5, fontWeight: 700, marginBottom: 8, lineHeight: 1.35, color: '#f2e6c9' }}>{b.title}</h3>
                    <p style={{ fontSize: 12.5, color: '#a99a72', lineHeight: 1.6 }}>{b.excerpt}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '34px 20px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: 14, color: '#a99a72' }}>
            <i className="ti ti-notes" style={{ fontSize: 28, marginBottom: 8, display: 'block', color: 'rgba(212,175,55,0.5)' }} />
            More stories from the gold collection coming soon.
          </div>
        )}
      </section>
    </div>
  );
}

const lbl = { fontSize: 12.5, fontWeight: 500, color: '#cbbf9e', display: 'block', marginBottom: 6 };
